import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, fieldIssues } from '../api';
import { ConflictNotice } from '../ui/ConflictNotice';
import { downloadInvoicePdf } from './pdfLink';
import type { Invoice, SendPreview } from './types';
import { DialogLayer } from '../ui/Dialog';
import { TestInvoiceMailNote } from './InvoiceQuestions';

type Form = { to: string; cc: string; subject: string; body: string };

/**
 * "Wyślij do klienta" (F35, as the quote's F30): the buyer's e-mail (editable), "DW", subject and text from the
 * template of "Dane do faktur" with the invoice's number, amount, due date, account and KSeF number. "Wyślij" queues
 * the e-mail with the stored PDF; the dialog is the confirmation. Without a mail server it says the client will not
 * get it.
 */
export function SendInvoiceDialog({ invoice, onDone, onClose, onReload }: { invoice: Invoice; onDone: (v: Invoice) => void; onClose: () => void; onReload: () => void }) {
  const preview = useQuery({ queryKey: ['invoices', 'send-preview', invoice.id], queryFn: () => api<SendPreview>(`/api/invoices/${invoice.id}/send-preview`), staleTime: Infinity, gcTime: 0 });
  // typed values win; until then the preview's
  const [typed, setTyped] = useState<Partial<Form>>({});
  const [local, setLocal] = useState('');
  const form: Form = { to: typed.to ?? preview.data?.to ?? '', cc: typed.cc ?? '', subject: typed.subject ?? preview.data?.subject ?? '', body: typed.body ?? preview.data?.body ?? '' };
  const send = useMutation({ mutationFn: () => api<Invoice>(`/api/invoices/${invoice.id}/send`, { method: 'POST', body: { ...form, version: invoice.version } }), onSuccess: onDone });
  function submit(e: FormEvent) {
    e.preventDefault();
    if (form.to.trim() === '') return setLocal('Wpisz adres e-mail klienta');
    if (form.subject.trim() === '') return setLocal('Wpisz temat wiadomości');
    setLocal('');
    send.mutate();
  }
  const error = send.error instanceof ApiError ? send.error : preview.error instanceof ApiError ? preview.error : null;
  // somebody changed or sent this invoice meanwhile: the screen is out of date
  const stale = !!error && (error.status === 409 || error.code === 'already_sent');
  const set = (patch: Partial<Form>) => setTyped((t) => ({ ...t, ...patch }));
  return (
    <div className="dialog-backdrop">
      <form className="dialog panel inv-send" role="dialog" aria-modal="true" aria-labelledby="inv-send-title" onSubmit={submit} data-feature-id="invoices.send">
        <DialogLayer onClose={onClose} busy={send.isPending} />
        <h2 id="inv-send-title">Wyślij fakturę {invoice.number} do klienta</h2>
        <p className="muted">E-mail z PDF faktury w załączniku.</p>
        {preview.data?.emailMode === 'log' && (
          <p className="alert" role="alert">
            Serwer poczty nie jest jeszcze ustawiony (SMTP_URL) – e-mail trafi tylko do dziennika serwera i klient go nie dostanie. Pobierz PDF i wyślij go ze swojej poczty albo poproś o ustawienie poczty.
          </p>
        )}
        <TestInvoiceMailNote invoice={invoice} />
        {stale && <ConflictNotice onReload={onReload} />}
        {preview.isLoading && (
          <p className="muted" role="status">
            Wczytywanie…
          </p>
        )}
        <label className="field">
          <span>Do</span>
          <input type="email" value={form.to} onChange={(e) => set({ to: e.target.value })} autoComplete="off" autoFocus placeholder="adres e-mail klienta" />
        </label>
        {!preview.isLoading && !preview.data?.to && !typed.to && <p className="hint">Nabywca nie ma adresu e-mail na fakturze – wpisz go tutaj.</p>}
        <label className="field">
          <span>DW (nieobowiązkowo)</span>
          <input type="email" value={form.cc} onChange={(e) => set({ cc: e.target.value })} autoComplete="off" placeholder="np. Twój adres, kopia dla siebie" />
        </label>
        <label className="field">
          <span>Temat</span>
          <input value={form.subject} maxLength={300} onChange={(e) => set({ subject: e.target.value })} autoComplete="off" />
        </label>
        <label className="field">
          <span>Treść</span>
          <textarea rows={9} value={form.body} maxLength={10000} onChange={(e) => set({ body: e.target.value })} />
        </label>
        <p className="inv-send-attachment">
          Załącznik: PDF faktury {invoice.number}{' '}
          <button className="btn small ghost" type="button" onClick={() => downloadInvoicePdf(invoice.id)}>
            Pobierz PDF
          </button>
        </p>
        <p className="hint">
          Szablon tematu i treści: <Link to="/finanse/ustawienia">Finanse → Ustawienia → E-mail z fakturą</Link>
        </p>
        {(local || (error && !stale)) && (
          <p className="alert" role="alert">
            {local || (error && (fieldIssues(error)[0]?.message ?? error.message))}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={send.isPending || preview.isLoading}>
            {send.isPending ? 'Wysyłanie…' : 'Wyślij'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
